/**
 * API hooks for candidates
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { API_ROUTES } from "@/constants/routes";
import type { CandidateListItem, Candidate } from "@/types/elections";

// Query keys factory
export const candidateKeys = {
    all: ['candidates'] as const,
    lists: () => [...candidateKeys.all, 'list'] as const,
    detail: (id: number) => [...candidateKeys.all, 'detail', id] as const,
};

const fetchCandidates = async (): Promise<CandidateListItem[]> => {
    const { data } = await api.get(API_ROUTES.candidates);
    // Handle both paginated ({results: []}) and non-paginated ([]) responses
    if (Array.isArray(data)) {
        return data;
    }
    if (data && Array.isArray(data.results)) {
        return data.results;
    }
    return [];
};

const fetchCandidate = async (id: number): Promise<Candidate> => {
    const { data } = await api.get(`${API_ROUTES.candidates}${id}/`);
    return data;
};

export function useCandidates() {
    return useQuery({
        queryKey: candidateKeys.lists(),
        queryFn: fetchCandidates,
        staleTime: 10 * 60 * 1000, // 10 minutes - candidates don't change often
        gcTime: 30 * 60 * 1000, // 30 minutes
    });
}

export function useCandidate(id: number | null) {
    return useQuery({
        queryKey: candidateKeys.detail(id!),
        queryFn: () => fetchCandidate(id!),
        enabled: id !== null,
        staleTime: 10 * 60 * 1000,
    });
}
